import React, { useState, useEffect, useRef } from 'react';
import {
  X,
  User,
  Shield,
  Plane,
  Car,
  HeartPulse,
  Briefcase,
  GraduationCap,
  Users,
  Home,
  Sparkles,
  Paperclip,
  Trash2,
  Lock,
  Unlock,
  Eye,
  EyeOff,
  Copy,
  Check,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Globe,
  FileText,
  AlertCircle,
  Share2,
  Download,
  Edit3,
  Clock,
  ExternalLink,
  Star,
  Printer,
} from 'lucide-react';

const IDENTITY_TYPE_ICONS = {
  personal: User,
  government_id: Shield,
  passport: Plane,
  driving_licence: Car,
  health_id: HeartPulse,
  work_id: Briefcase,
  student_id: GraduationCap,
  family_member: Users,
  address_id: Home,
  custom: Sparkles,
};

const IDENTITY_TYPE_NAMES = {
  personal: 'Personal Identity',
  government_id: 'Government ID / SSN',
  passport: 'Passport',
  driving_licence: 'Driving Licence',
  health_id: 'Health & Insurance ID',
  work_id: 'Work Identity',
  student_id: 'Student ID',
  family_member: 'Family Member',
  address_id: 'Address Identity',
  custom: 'Custom Identity',
};

export default function IdentityViewModal({
  open,
  identity,
  onClose,
  onEdit,
  onToggleFavorite,
  onDelete,
  onShare,
  hasEditPermission = true,
}) {
  const [showDocNumber, setShowDocNumber] = useState(false);
  const [docNumberCopied, setDocNumberCopied] = useState(false);
  const [copiedField, setCopiedField] = useState(null);
  const [pinEntered, setPinEntered] = useState('');
  const [isPinUnlocked, setIsPinUnlocked] = useState(false);
  const [pinError, setPinError] = useState('');
  const autoHideTimerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setShowDocNumber(false);
      setIsPinUnlocked(false);
      setPinEntered('');
      setPinError('');
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    }
  }, [open]);

  if (!open || !identity) return null;

  const f = identity.fields || identity;
  const addr = f.address || {};
  const IconComponent = IDENTITY_TYPE_ICONS[identity.identityType] || User;

  // Sensitive field reveal toggle with 15s auto-hide
  const toggleDocNumberVisibility = () => {
    if (identity.isLocked && !isPinUnlocked) {
      // Need PIN first
      return;
    }

    if (!showDocNumber) {
      setShowDocNumber(true);
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
      autoHideTimerRef.current = setTimeout(() => {
        setShowDocNumber(false);
      }, 15000);
    } else {
      setShowDocNumber(false);
      if (autoHideTimerRef.current) clearTimeout(autoHideTimerRef.current);
    }
  };

  const handleUnlockPin = (e) => {
    e.preventDefault();
    if (pinEntered === identity.lockPin) {
      setIsPinUnlocked(true);
      setPinError('');
      setShowDocNumber(true);
      autoHideTimerRef.current = setTimeout(() => {
        setShowDocNumber(false);
      }, 15000);
    } else {
      setPinError('Incorrect PIN. Please verify and try again.');
    }
  };

  const copyToClipboard = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  // Expiry calculation
  const getExpiryStatus = () => {
    if (!f.expiryDate) return null;
    const now = new Date();
    const expiry = new Date(f.expiryDate);
    if (isNaN(expiry.getTime())) return null;

    const diffMs = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { status: 'expired', label: `Expired (${Math.abs(diffDays)} days ago)`, color: '#ef4444' };
    }
    if (diffDays <= 30) {
      return { status: 'expiring_soon', label: `Expiring Soon (${diffDays} days left)`, color: '#f59e0b' };
    }
    if (diffDays <= 90) {
      return { status: 'expiring_upcoming', label: `Expiring in ${diffDays} days`, color: '#3b82f6' };
    }
    return { status: 'valid', label: `Valid until ${new Date(f.expiryDate).toLocaleDateString()}`, color: '#10b981' };
  };

  const expiryBadge = getExpiryStatus();

  // Mask string representation
  const getMaskedDocNumber = (val) => {
    if (!val) return '—';
    if (val.length <= 4) return '••••';
    const last4 = val.slice(-4);
    return `•••• •••• •••• ${last4}`;
  };

  // Formatted address
  const fullAddress = [addr.street, addr.city, addr.state || addr.stateProvince, addr.postalCode, addr.country]
    .filter(Boolean)
    .join(', ');

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="card-drawer-wrapper" role="presentation">
      <div className="card-drawer-overlay animate-fade-in" onClick={onClose} aria-hidden="true" />
      <aside
        className="card-drawer identity-drawer ios-identity-drawer identity-view-drawer animate-slide-left"
        role="dialog"
        aria-modal="true"
        aria-label={identity.title || 'Identity Details'}
      >
        {/* Header */}
        <div className="card-drawer-header modal-header identity-view-header">
          <div className="identity-view-title-group">
            <div className="identity-header-avatar">
              <IconComponent size={20} />
            </div>
            <div className="identity-view-header-text">
              <h2 className="modal-title">
                {identity.title}
              </h2>
              <div className="identity-view-meta-row">
                <span className="badge badge-primary">
                  {IDENTITY_TYPE_NAMES[identity.identityType] || 'Identity'}
                </span>
                <span className="badge badge-secondary">{identity.category || 'Personal'}</span>
                {expiryBadge && (
                  <span
                    className="identity-expiry-status-pill"
                    style={{
                      backgroundColor: `${expiryBadge.color}15`,
                      color: expiryBadge.color,
                      borderColor: `${expiryBadge.color}35`,
                    }}
                  >
                    <Clock size={11} />
                    {expiryBadge.label}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="identity-view-header-actions">
            {onToggleFavorite && (
              <button
                type="button"
                className={`btn-icon btn-sm ${Boolean(identity.is_favorite || identity.isFavorite) ? 'text-warning' : ''}`}
                onClick={() => onToggleFavorite(identity)}
                title={Boolean(identity.is_favorite || identity.isFavorite) ? 'Remove from favorites' : 'Add to favorites'}
              >
                <Star
                  size={15}
                  fill={Boolean(identity.is_favorite || identity.isFavorite) ? 'currentColor' : 'none'}
                />
              </button>
            )}
            <button
              type="button"
              className="btn-icon btn-sm"
              onClick={handlePrint}
              title="Print Identity Summary"
            >
              <Printer size={15} />
            </button>
            <button
              type="button"
              className="modal-close-btn"
              onClick={onClose}
              aria-label="Close view dialog"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div className="card-drawer-body modal-body identity-view-body">
          {/* Sensitive Document Box */}
          <div className="identity-doc-box">
            <div className="identity-doc-box-header">
              <div className="identity-doc-box-title">
                <Shield size={16} className="text-primary" />
                <span>OFFICIAL IDENTIFICATION NUMBER</span>
              </div>
              {f.documentNumber && (
                <span className="identity-auto-hide-note">
                  {showDocNumber ? 'Auto-hiding in 15s' : 'Masked for security'}
                </span>
              )}
            </div>

            {identity.isLocked && !isPinUnlocked ? (
              <form onSubmit={handleUnlockPin} className="identity-pin-gate">
                <Lock size={20} className="text-warning" />
                <p>This identity is PIN protected. Enter PIN to reveal sensitive numbers.</p>
                <div className="identity-pin-inputs">
                  <input
                    type="password"
                    maxLength={8}
                    className="form-input"
                    placeholder="Enter PIN"
                    value={pinEntered}
                    onChange={(e) => setPinEntered(e.target.value)}
                  />
                  <button type="submit" className="btn btn-primary btn-sm">
                    Unlock
                  </button>
                </div>
                {pinError && <span className="text-danger text-sm">{pinError}</span>}
              </form>
            ) : (
              <div className="identity-doc-box-content">
                <div className="identity-doc-value">
                  {showDocNumber ? f.documentNumber || '—' : getMaskedDocNumber(f.documentNumber)}
                </div>

                <div className="identity-doc-actions">
                  {f.documentNumber && (
                    <>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={toggleDocNumberVisibility}
                      >
                        {showDocNumber ? <EyeOff size={14} /> : <Eye size={14} />}
                        <span>{showDocNumber ? 'Hide' : 'Reveal'}</span>
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary btn-sm"
                        onClick={() => copyToClipboard(f.documentNumber, 'docNumber')}
                      >
                        {copiedField === 'docNumber' ? (
                          <Check size={14} className="text-success" />
                        ) : (
                          <Copy size={14} />
                        )}
                        <span>{copiedField === 'docNumber' ? 'Copied' : 'Copy'}</span>
                      </button>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section: Personal Information */}
          <div className="identity-view-section">
            <h4 className="identity-view-section-title">
              <User size={15} />
              <span>Personal Details</span>
            </h4>
            <div className="identity-info-grid">
              <div className="identity-info-cell">
                <label>Full Legal Name</label>
                <div className="identity-cell-value">
                  <span>{f.fullName || '—'}</span>
                  {f.fullName && (
                    <button
                      type="button"
                      className="cell-copy-btn"
                      onClick={() => copyToClipboard(f.fullName, 'fullName')}
                      title="Copy full name"
                    >
                      {copiedField === 'fullName' ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                    </button>
                  )}
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Date of Birth</label>
                <div className="identity-cell-value">
                  <span>{f.dob ? new Date(f.dob).toLocaleDateString() : '—'}</span>
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Nationality</label>
                <div className="identity-cell-value">
                  <span>{f.nationality || '—'}</span>
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Gender</label>
                <div className="identity-cell-value">
                  <span>{f.gender || '—'}</span>
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Phone</label>
                <div className="identity-cell-value">
                  <span>{f.phone || '—'}</span>
                  {f.phone && (
                    <button
                      type="button"
                      className="cell-copy-btn"
                      onClick={() => copyToClipboard(f.phone, 'phone')}
                      title="Copy phone"
                    >
                      {copiedField === 'phone' ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                    </button>
                  )}
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Email</label>
                <div className="identity-cell-value">
                  <span>{f.email || '—'}</span>
                  {f.email && (
                    <button
                      type="button"
                      className="cell-copy-btn"
                      onClick={() => copyToClipboard(f.email, 'email')}
                      title="Copy email"
                    >
                      {copiedField === 'email' ? <Check size={12} className="text-success" /> : <Copy size={12} />}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Section: Official Validity & Issuing Data */}
          <div className="identity-view-section">
            <h4 className="identity-view-section-title">
              <Shield size={15} />
              <span>Issuance & Validity</span>
            </h4>
            <div className="identity-info-grid">
              <div className="identity-info-cell">
                <label>Issuing Authority</label>
                <div className="identity-cell-value">
                  <span>{f.issuingAuthority || '—'}</span>
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Issue Date</label>
                <div className="identity-cell-value">
                  <span>{f.issueDate ? new Date(f.issueDate).toLocaleDateString() : '—'}</span>
                </div>
              </div>

              <div className="identity-info-cell">
                <label>Expiry Date</label>
                <div className="identity-cell-value">
                  <span>{f.expiryDate ? new Date(f.expiryDate).toLocaleDateString() : 'No Expiry'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Section: Address */}
          {fullAddress && (
            <div className="identity-view-section">
              <div className="identity-section-header-flex">
                <h4 className="identity-view-section-title">
                  <MapPin size={15} />
                  <span>Address Details</span>
                </h4>
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  onClick={() => copyToClipboard(fullAddress, 'address')}
                >
                  {copiedField === 'address' ? <Check size={13} className="text-success" /> : <Copy size={13} />}
                  <span>{copiedField === 'address' ? 'Copied' : 'Copy Full Address'}</span>
                </button>
              </div>

              <div className="identity-address-box">
                <p className="identity-address-text">{fullAddress}</p>
              </div>
            </div>
          )}

          {/* Section: Document Attachments */}
          {Array.isArray(identity.attachments) && identity.attachments.length > 0 && (
            <div className="identity-view-section">
              <h4 className="identity-view-section-title">
                <Paperclip size={15} />
                <span>Encrypted Document Scans ({identity.attachments.length})</span>
              </h4>
              <div className="identity-view-attachments-grid">
                {identity.attachments.map((att) => {
                  const isImage = att.type?.startsWith('image/');
                  const sizeKb = (att.size / 1024).toFixed(1);
                  return (
                    <div key={att.id} className="identity-view-attachment-item">
                      <div className="identity-view-att-preview">
                        {isImage && att.dataUrl ? (
                          <img src={att.dataUrl} alt={att.name} className="identity-view-att-thumb" />
                        ) : (
                          <FileText size={28} className="text-primary" />
                        )}
                      </div>
                      <div className="identity-view-att-meta">
                        <span className="identity-view-att-name" title={att.name}>
                          {att.name}
                        </span>
                        <span className="text-secondary text-sm">{sizeKb} KB</span>
                      </div>
                      {att.dataUrl && (
                        <a
                          href={att.dataUrl}
                          download={att.name}
                          className="btn btn-secondary btn-sm"
                          title="Download decrypted document"
                        >
                          <Download size={13} />
                          <span>Save</span>
                        </a>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Section: Notes */}
          {f.notes && (
            <div className="identity-view-section">
              <h4 className="identity-view-section-title">
                <FileText size={15} />
                <span>Confidential Notes</span>
              </h4>
              <div className="identity-notes-box">
                <p>{f.notes}</p>
              </div>
            </div>
          )}

          {/* Tags */}
          {Array.isArray(identity.tags) && identity.tags.length > 0 && (
            <div className="identity-view-tags-row">
              {identity.tags.map((t) => (
                <span key={t} className="tag-chip">
                  #{t}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Modal Footer with generous padding */}
        <div className="card-drawer-footer modal-footer identity-view-footer">
          <div className="identity-view-footer-left">
            {onDelete && (
              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={() => onDelete(identity)}
              >
                <Trash2 size={14} />
                <span>Move to Trash</span>
              </button>
            )}
          </div>
          <div className="identity-view-footer-right">
            <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>
              Close
            </button>
            {hasEditPermission && onEdit && (
              <button
                type="button"
                className="btn btn-primary btn-sm"
                onClick={() => {
                  onClose();
                  onEdit(identity);
                }}
              >
                <Edit3 size={14} />
                <span>Edit Identity</span>
              </button>
            )}
          </div>
        </div>
      </aside>
    </div>
  );
}
